import React, { useState, useRef, useContext } from "react";
import "./izvestaji.css";
import { baseRequest } from "../../../axiosconfig";
import Loading from "../../loading";
import DropDown from "../../elements/dropDown";
import { useReactToPrint } from "react-to-print";
import { PRINT_PAGE_STYLE } from "../../../utils/printPageStyle";
import { UserContext } from "../../../userContext";
import { ROLES } from "../../../constants/roles";
import { formatDeadline } from "../../../utils/deadlineFormat";

const LEVEL_LABELS = { nizak: "Nizak", srednji: "Srednji", visok: "Visok" };
const MEASURE_LABELS = {
  zavrseno: "Završeno",
  probijen_rok: "Probijen rok",
  istice_uskoro: "Ističe uskoro",
  u_roku: "U roku",
  bez_roka: "Bez roka",
};

const riskLevelFilterList = [
  { id: "nizak", name: "Nizak" },
  { id: "srednji", name: "Srednji" },
  { id: "visok", name: "Visok" },
];

const measureStatusFilterList = Object.entries(MEASURE_LABELS).map(
  ([id, name]) => ({ id, name })
);

function Izvestaji() {
  const { user } = useContext(UserContext);
  const isAdmin = user && user.role_id === ROLES.SUPER_ADMIN;

  const reportTypes = [
    { id: "process_overview", name: "Pregled poslovnih procesa" },
    { id: "risks_by_level", name: "Rizici po nivou" },
    { id: "measures_by_status", name: "Mere po statusu" },
    ...(isAdmin ? [{ id: "user_activity", name: "Aktivnosti korisnika" }] : []),
  ];

  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState(null);
  const [filterValue, setFilterValue] = useState({ id: "all", name: "ALL" });
  const [result, setResult] = useState(null);
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: PRINT_PAGE_STYLE,
  });

  const filterListFor = (type) => {
    if (type === "risks_by_level") return riskLevelFilterList;
    if (type === "measures_by_status") return measureStatusFilterList;
    return null;
  };

  const handleGenerate = () => {
    if (!reportType) return;
    setLoading(true);
    const params = {};
    if (filterValue.id !== "all") {
      if (reportType.id === "risks_by_level") params.risk_level = filterValue.id;
      if (reportType.id === "measures_by_status") params.status = filterValue.id;
    }
    baseRequest
      .get(`/reports/${reportType.id}`, { params })
      .then((res) => {
        setResult({ type: reportType.id, name: reportType.name, data: res.data });
        setLoading(false);
      })
      .catch(() => {
        setResult(null);
        setLoading(false);
      });
  };

  const renderResultTable = () => {
    if (!result) return null;
    const { type, data } = result;

    if (type === "process_overview") {
      return (
        <table className="izvestaji-table">
          <thead>
            <tr>
              <th>ŠIFRA</th>
              <th>POSLOVNI PROCES</th>
              <th>ORG. JEDINICA</th>
              <th>VLASNICI</th>
              <th>MAPE (ODOBRENE)</th>
              <th>PROCEDURE</th>
              <th>AKTIVNOSTI</th>
              <th>RIZICI</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td colSpan={8} className="izvestaji-empty">Nema podataka.</td></tr>
            )}
            {data.rows.map((r) => (
              <tr key={r.id}>
                <td>{r.code || "—"}</td>
                <td>{r.name}</td>
                <td>{r.organization_unit_name || "—"}</td>
                <td>{r.owners || "—"}</td>
                <td>{r.maps_count} ({r.maps_approved_count})</td>
                <td>{r.procedures_count}</td>
                <td>{r.activities_count}</td>
                <td>{r.risks_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (type === "risks_by_level") {
      return (
        <>
          <p className="izvestaji-summary">
            Nizak: {data.summary.nizak} | Srednji: {data.summary.srednji} |
            Visok: {data.summary.visok} | Bez ocene: {data.summary.bez_ocene}
          </p>
          <table className="izvestaji-table">
            <thead>
              <tr>
                <th>BROJ</th>
                <th>RIZIK</th>
                <th>PROCES</th>
                <th>GRUPA</th>
                <th>VEROVATNOĆA</th>
                <th>UTICAJ</th>
                <th>UKUPNO</th>
                <th>NIVO</th>
                <th>VLASNICI</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={9} className="izvestaji-empty">Nema podataka.</td></tr>
              )}
              {data.rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.number || "—"}</td>
                  <td>{r.name}</td>
                  <td>{r.process_name || "—"}</td>
                  <td>{r.group_name || "—"}</td>
                  <td>{r.probability || "—"}</td>
                  <td>{r.influence || "—"}</td>
                  <td>{r.total !== null ? `${r.total} (${r.percentage})` : "—"}</td>
                  <td>{LEVEL_LABELS[r.risk_level] || "—"}</td>
                  <td>{r.owners || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    }

    if (type === "measures_by_status") {
      return (
        <>
          <p className="izvestaji-summary">
            {Object.entries(MEASURE_LABELS)
              .map(([key, label]) => `${label}: ${data.summary[key] || 0}`)
              .join(" | ")}
          </p>
          <table className="izvestaji-table">
            <thead>
              <tr>
                <th>RIZIK</th>
                <th>OPIS MERE</th>
                <th>PROCES</th>
                <th>ODGOVORAN</th>
                <th>ROK</th>
                <th>STATUS ROKA</th>
                <th>STATUS IZVRŠENJA</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={7} className="izvestaji-empty">Nema podataka.</td></tr>
              )}
              {data.rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.description || "—"}</td>
                  <td>{r.process_name || "—"}</td>
                  <td>{r.owners || "—"}</td>
                  <td>{formatDeadline(r.deadline)}</td>
                  <td>{MEASURE_LABELS[r.computed_status]}</td>
                  <td>{r.status_text || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    }

    if (type === "user_activity") {
      return (
        <>
          <table className="izvestaji-table" style={{ maxWidth: "600px" }}>
            <thead>
              <tr>
                <th>KORISNIK</th>
                <th>UKUPNO AKTIVNOSTI</th>
              </tr>
            </thead>
            <tbody>
              {data.summary.length === 0 && (
                <tr><td colSpan={2} className="izvestaji-empty">Nema podataka.</td></tr>
              )}
              {data.summary.map((u) => (
                <tr key={u.user_name}>
                  <td>{u.user_name}</td>
                  <td>{u.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="izvestaji-summary">
            Detaljan pregled aktivnosti dostupan je na strani "Evidencija
            aktivnosti".
          </p>
        </>
      );
    }

    return null;
  };

  const currentFilterList = reportType ? filterListFor(reportType.id) : null;

  return (
    <div className="izvestaji">
      <h2 className="izvestaji-title">IZVEŠTAJI PO ZAHTEVU RUKOVODSTVA</h2>

      <div className="izvestaji-controls">
        <DropDown
          dropDownStyle={{ border: "1px solid var(--mainColor)", width: "300px" }}
          height={"49px"}
          list={reportTypes}
          onChange={({ value }) => {
            setReportType(value.id === "all" ? null : value);
            setFilterValue({ id: "all", name: "ALL" });
            setResult(null);
          }}
          selectedValue={reportType?.name || "Izaberite izveštaj"}
          isFilter={false}
        />
        {currentFilterList && (
          <DropDown
            dropDownStyle={{ border: "1px solid var(--mainColor)", width: "220px" }}
            height={"49px"}
            list={currentFilterList}
            onChange={({ value }) => setFilterValue(value)}
            selectedValue={filterValue?.name}
            isFilter={true}
          />
        )}
        <button
          className="add-poslovni-proces-btn"
          onClick={handleGenerate}
          disabled={!reportType}
        >
          <span>Generiši izveštaj</span>
        </button>
        {result && (
          <button className="add-btn-large" onClick={handlePrint}>
            <img alt="" src="/images/printer.png"></img>
          </button>
        )}
      </div>

      {result && (
        <>
          <h3 className="izvestaji-result-title">{result.name}</h3>
          {renderResultTable()}
          <div style={{ display: "none" }}>
            <div ref={componentRef} className="izvestaji-print">
              <h2>{result.name}</h2>
              <p>Datum: {new Date().toLocaleDateString("sr-RS")}</p>
              {renderResultTable()}
            </div>
          </div>
        </>
      )}

      <Loading isLoading={loading} />
    </div>
  );
}

export default Izvestaji;
